import {
    select as D3select,
    dispatch as D3dispatch,
    scaleTime as D3scaleTime,
    scaleLinear as D3scaleLinear,
    scaleOrdinal as D3scaleOrdinal,
    axisBottom as D3axisBottom,
    axisLeft as D3axisLeft,
    min as D3min,
    max as D3max,
    format as D3format,
} from 'd3';
import Moment from 'moment';
import D3Tooltips from './D3Tooltips.js';
import { debounce } from 'lodash';

// a d3 timeline chart component
// expects data = [{timestamp, count}]
// can provide dataservice with normalizing functions
const D3TimelineBarChart = function () {
    const dispatch = D3dispatch('mouseover', 'click');

    // default settings can be overridden
    let height = 100;
    let width = 100;
    let dataservice = {
        timestamp: d => d.timestamp,
        count: d => d.count
    };
    let color = D3scaleOrdinal();
    let max;
    let theme;

    // make room for axis and tooltips
    const margin = { top: 20, right: 0, bottom: 20, left: 25 };

    // time and number formatting
    const nformat = D3format(',.0f');

    function my (selection) {
        if (!selection) return;
        selection.each(function (data, i) {
            const ymax = max || D3max(data, function (d) { return dataservice.count(d) });
            const xmin = D3min(data, function (d) { return dataservice.timestamp(d) });
            const xmax = D3max(data, function (d) { return dataservice.end(d) });
            const bw = width / ((data || []).length || 1);
            const barwidth = bw - (bw > 5 ? 5 : Math.floor(bw));
            const x = D3scaleTime()
                .range([margin.left, width - margin.right - margin.left])
                .domain([xmin, xmax]);
            const y = D3scaleLinear()
                .range([height - margin.bottom, margin.top])
                .domain([0, D3max([ymax, 1])]);

            // slow down time range selections
            const onmouseover = debounce(selection => dispatch.call('mouseover', my, selection), 110);
            const onclick = selection => dispatch.call('click', my, selection);

            const sv = D3select(this).selectAll('svg').data(['once']);
            const svg = sv.enter()
                .append('svg')
                .merge(sv)
                .style('overflow', 'visible')
                .attr('width', width)
                .attr('height', height);

            // first add the 'g' that will contain the bars so that they are behind anything else that gets added
            const chartg = svg.selectAll('g.chart')
                .data(['chart'])
                .join('g')
                .attr('class', 'chart');

            // next initialize the tooltips with an arrow
            // + set boundaries to allow the tooltip to scoot at the edges of the chart
            const tip = D3Tooltips()
                .theme(theme)
                .arrow(true)
                .offset(0.25)
                .bounds({ width: width - margin.left - margin.right, height });

            // enter and merge separately for nicer animation
            const join = chartg.selectAll('rect.bar').data(data, d => dataservice.timestamp(d));
            const jenter = join.enter().append('rect')
                .attr('class', 'bar')
                .attr('y', y.range()[0])
                .attr('height', 0)
                .style('cursor', 'pointer');

            jenter.merge(join)
                .classed('current', d => dataservice.end(d) > Date.now())
                .attr('width', barwidth)
                .on('mouseover', function (e, d, i) {
                    D3select(this).style('opacity', 0.5);
                    // display, position and text for the tooltip
                    tip.move(
                        x(dataservice.timestamp(d)) + barwidth / 2,
                        y(dataservice.count(d)),
                        [[
                            Moment(dataservice.timestamp(d)).format(theme.date.short) +
                            ' - ' + Moment(dataservice.end(d)).format(theme.date.short),
                            nformat(dataservice.count(d))
                        ]]);
                    // dispatch event
                    onmouseover(d);
                })
                .on('mouseout', function (e, d) {
                    D3select(this).style('opacity', 1);
                    tip.hide();
                    // dispatch event
                    onmouseover();
                })
                .on('click', function (e, d) {
                    tip.hide();
                    // dispatch event
                    onclick(d);
                })
                // .transition()
                // .duration(theme.transitions.duration.complex)
                .attr('x', d => x(dataservice.timestamp(d)))
                .transition()
                .duration(theme.transitions.duration.complex)
                .delay((d, i) => i * 10)
                .style('fill', d => color(d.key))
                .attr('y', d => y(dataservice.count(d)))
                .attr('height', d => y.range()[0] - y(dataservice.count(d)));

            // old data rolls off the chart
            join.exit().remove();

            // x-axis
            svg.selectAll('g.x-axis').data(['x-axis']).join('g')
                .attr('class', 'x-axis')
                .attr('transform', `translate(0,${height - margin.bottom})`)
                // .transition()
                // .duration(theme.transitions.duration.complex)
                .call(D3axisBottom(x));

            // y-axis
            svg.selectAll('g.y-axis').data(['y-axis']).join('g')
                .attr('class', 'y-axis')
                .attr('transform', `translate(${margin.left},${0})`)
                .call(D3axisLeft(y).ticks(5))
                .selectAll('text').style('text-anchor', 'end');

            // tooltips
            svg.selectAll('g.timeline-tip').data(['timeline-tip']).join('g')
                .attr('class', 'timeline-tip')
                .attr('transform', `translate(${0},${-margin.bottom})`)
                .call(tip);
        });
    }

    my.width = function (v) {
        if (!arguments.length) return width;
        width = v;
        return my;
    };

    my.height = function (v) {
        if (!arguments.length) return height;
        height = v;
        return my;
    };

    my.dataservice = function (v) {
        if (!arguments.length) return dataservice;
        dataservice = v;
        return my;
    };

    my.color = function (v) {
        if (!arguments.length) return color;
        color = v;
        return my;
    };

    my.max = function (v) {
        if (!arguments.length) return max;
        max = v;
        return my;
    };

    my.theme = function (v) {
        if (!arguments.length) return theme;
        theme = v || theme;
        return my;
    };

    my.on = function () {
        const value = dispatch.on.apply(dispatch, arguments);
        return value === dispatch ? my : value;
    };

    return my;
};

export default D3TimelineBarChart;
